import React, {useCallback, useContext} from 'react';


import {FormInput, NumberInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';

import FormSection from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {LocaleContext} from '@pb/kit-context-locale';

import {
    CLIENT_URL_CONTROL,
    INVALID_CLIENT_URL,
    INVALID_CLIENT_URL_MESSAGE,
} from './constants';
import {DealCloudSectionSkeleton} from './deal-cloud-skeleton';

import type {Dispatch, SetStateAction} from 'react';
import type {DealcloudSettings} from 'src/openapi/admin/models/DealcloudSettings';
import type {GetCrmLimit} from 'src/openapi/admin/models/GetCrmLimit';

type Props = {
    isLoading: boolean,
    limits: GetCrmLimit,
    credentials: DealcloudSettings,

    onChangeCredentials: Dispatch<DealcloudSettings>,
    onChangeLimits: Dispatch<SetStateAction<GetCrmLimit>>,
    disabled?: boolean,
    readonly?: boolean,
};


const validationRegex = /^https:\/\/[\w-.+]{2,256}\.[a-z]{2,6}\b([\w-@:%+.~#?&/=]*)$/;

export const DealCloudSection = (props: Props) => {
    const {
        isLoading,
        limits,
        credentials,
        onChangeLimits,
        onChangeCredentials,
        disabled,
        readonly,
    } = props;

    const {addError, removeError} = useContext(ValidationContext);
    const {locale} = useContext(LocaleContext);

    const changeMaxUsers = useCallback((maxUsers?: number) => {
        onChangeLimits(prevState => ({...prevState, maxUsers: maxUsers || 0}));
    }, [onChangeLimits]);

    const changeCredentials = useCallback((clientUrl: string) => {
        onChangeCredentials({value: clientUrl});

        if (!clientUrl || (clientUrl.match(validationRegex) && !clientUrl.includes(' '))) {
            removeError(CLIENT_URL_CONTROL);
        } else {
            addError({
                code: INVALID_CLIENT_URL,
                message: INVALID_CLIENT_URL_MESSAGE,
                controlId: CLIENT_URL_CONTROL,
            });
        }
    }, [addError, removeError, onChangeCredentials]);

    return (
        <Skeleton shown={isLoading} view={<DealCloudSectionSkeleton/>}>
            <FormSection headline="Credentials">
                <CustomValidatedControl name={CLIENT_URL_CONTROL}>
                    {error => (
                        <FormInput
                            width={300}
                            label="Client URL"
                            placeholder="DealCloud URL"
                            readOnly={readonly}
                            disabled={disabled}
                            value={credentials.value || ''}
                            onBlur={changeCredentials}
                            valid={error ? false : undefined}
                            errorDescription={error}
                        />
                    )}
                </CustomValidatedControl>
            </FormSection>
            <FormSection headline="CRM Limits">
                <ResponsiveElementGroup>
                    <NumberInput
                        width={300}
                        label={<span>DealCloud users*</span>}
                        onBlur={changeMaxUsers}
                        readOnly={readonly}
                        placeholder="Max DealCloud users"
                        disabled={disabled}
                        value={limits.maxUsers}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.maxUsers, locale)}
                    />
                </ResponsiveElementGroup>
            </FormSection>
        </Skeleton>
    );
};
